import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { HiOutlineMenuAlt4 } from "react-icons/hi";
import { FaSearch, FaUser, FaCaretDown, FaShoppingCart, FaTimes, FaSignOutAlt, FaSignInAlt, FaUserCircle } from "react-icons/fa";
import Flex from "../../designLayouts/Flex";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { BsSuitHeartFill } from "react-icons/bs";
import { fetchProductsList } from "../../../constants/productsList";
import { useAuth } from "../../../context/AuthContext";
import { toast } from "react-toastify";
import { setSelectedCategoryOnly } from "../../../redux/orebiSlice";
import { db } from "../../firebaseConfig/firebaseConfig";
import { doc, onSnapshot } from "firebase/firestore";

import { CATEGORIES } from "../../../config/categories";

const HeaderBottom = () => {
  const products = useSelector((state) => state.orebiReducer.products);
  const dispatch = useDispatch();
  const [show, setShow] = useState(false);
  const [showUser, setShowUser] = useState(false);
  const [buyingOptionsEnabled, setBuyingOptionsEnabled] = useState(true);
  const navigate = useNavigate();
  const ref = useRef();
  const { currentUser, userProfile, logout } = useAuth();

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "config", "publicSettings"),
      (docSnap) => {
        if (docSnap.exists()) {
          setBuyingOptionsEnabled(docSnap.data().buyingOptionsEnabled !== false);
        }
      },
      (error) => {
        console.error("Error fetching settings in HeaderBottom:", error);
      }
    );
    return () => unsub();
  }, []);

  // Click outside to close category dropdown
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setShow(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // ---- Live Firestore Search ----
  const [searchQuery, setSearchQuery] = useState("");
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);

  // Load products from Firestore once on mount for search
  useEffect(() => {
    fetchProductsList().then((list) => setAllProducts(list));
  }, []);

  // Filter live as user types
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredProducts([]);
      return;
    }
    const q = searchQuery.toLowerCase();
    const filtered = allProducts.filter(
      (item) =>
        (item.productName && item.productName.toLowerCase().includes(q)) ||
        (item.cat && item.cat.toLowerCase().includes(q)) ||
        (item.brand && item.brand.toLowerCase().includes(q)) ||
        (item.tags && item.tags.some((t) => t.toLowerCase().includes(q)))
    );
    setFilteredProducts(filtered.slice(0, 8)); // Cap at 8 results
  }, [searchQuery, allProducts]);

  const handleSearchSelect = (item) => {
    const slugify = (name) => {
      return String(name)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)+/g, "");
    };
    navigate(
      `/product/${slugify(item.productName)}`,
      { state: { item } }
    );
    setSearchQuery("");
    setFilteredProducts([]);
  };

  return (
    <div className="w-full bg-[#F5F5F3] relative">
      <div className="max-w-container mx-auto">
        <Flex className="flex flex-col lg:flex-row items-start lg:items-center justify-between w-full px-4 pb-4 lg:pb-0 h-full lg:h-24 gap-4">

          {/* Shop by Category Dropdown */}
          <div
            ref={ref}
            className="relative flex h-14 cursor-pointer items-center gap-2 text-primeColor"
            onClick={() => setShow(!show)}
          >
            <HiOutlineMenuAlt4 className="w-5 h-5" />
            <p className="text-[14px] font-normal whitespace-nowrap">Shop by Category</p>

            {show && (
              <motion.ul
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="absolute top-14 left-0 z-50 bg-primeColor w-56 text-white shadow-xl rounded-b-lg overflow-hidden"
              >
                {CATEGORIES.map((cat) => (
                  <Link
                    key={cat.path}
                    to="/shop"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShow(false);
                      dispatch(setSelectedCategoryOnly({ _id: cat._id, title: cat.title }));
                    }}
                  >
                    <li className="px-5 py-2.5 text-sm border-b border-white border-opacity-10 hover:bg-white hover:text-primeColor transition-colors duration-200 cursor-pointer">
                      {cat.title}
                    </li>
                  </Link>
                ))}
              </motion.ul>
            )}
          </div>

          {/* Search Bar - Live Firestore */}
          <div className="relative w-full lg:w-[600px] h-[50px] text-base text-primeColor bg-white flex items-center gap-2 justify-between px-6 rounded-xl shadow-sm">
            <FaSearch className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <input
              className="flex-1 h-full outline-none placeholder:text-[#C4C4C4] placeholder:text-[14px] text-sm"
              type="text"
              onChange={(e) => setSearchQuery(e.target.value)}
              value={searchQuery}
              placeholder="Search products or categories..."
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="text-gray-400 hover:text-red-500 transition-colors">
                <FaTimes className="w-3 h-3" />
              </button>
            )}

            {/* Search Results Dropdown */}
            {searchQuery && (
              <div className="w-full absolute top-14 left-0 bg-white z-50 shadow-2xl rounded-xl overflow-hidden border border-gray-100 max-h-96 overflow-y-auto">
                {filteredProducts.length === 0 ? (
                  <div className="p-6 text-center text-gray-400 text-sm">
                    No products found for "<strong>{searchQuery}</strong>"
                  </div>
                ) : (
                  <>
                    <div className="px-4 py-2 bg-gray-50 border-b text-xs text-gray-400 font-semibold uppercase tracking-wider">
                      {filteredProducts.length} result{filteredProducts.length !== 1 ? "s" : ""}
                    </div>
                    {filteredProducts.map((item) => (
                      <div
                        key={item._id}
                        onClick={() => handleSearchSelect(item)}
                        className="flex items-center gap-4 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors"
                      >
                        <img
                          src={item.img}
                          alt={item.productName}
                          className="w-12 h-12 object-contain rounded-lg bg-gray-100 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-gray-800 truncate">{item.productName}</p>
                          <p className="text-xs text-gray-400">{item.cat} · {item.brand}</p>
                        </div>
                        <div className="flex-shrink-0 text-right">
                           {item.discount > 0 ? (
                            <>
                              <p className="text-xs text-gray-400 line-through">LKR {Number(item.price).toLocaleString("en-LK")}</p>
                              <p className="text-sm font-bold text-primeColor">LKR {Number(item.discountedPrice).toLocaleString("en-LK")}</p>
                            </>
                          ) : (
                            <p className="text-sm font-bold text-primeColor">LKR {Number(item.price).toLocaleString("en-LK")}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right Icons */}
          <div className="flex gap-4 mt-2 lg:mt-0 items-center pr-6 cursor-pointer relative">
            {/* User menu */}
            <div onClick={() => setShowUser(!showUser)} className="flex items-center gap-1 hover:text-primeColor transition-colors">
              {currentUser ? (
                <div className="flex items-center gap-1.5">
                  <div className="w-7 h-7 rounded-full bg-primeColor text-white flex items-center justify-center text-xs font-bold">
                    {(currentUser.displayName || currentUser.email || "U")[0].toUpperCase()}
                  </div>
                  <span className="text-xs font-medium text-gray-700 hidden md:block max-w-[80px] truncate">
                    {currentUser.displayName || "Account"}
                  </span>
                </div>
              ) : (
                <>
                  <FaUser />
                  <FaCaretDown className="text-xs" />
                </>
              )}
            </div>

            {showUser && (
              <motion.ul
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="absolute top-10 left-0 z-50 bg-white border border-gray-100 shadow-xl w-52 rounded-xl overflow-hidden"
              >
                {currentUser ? (
                  <>
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-800 truncate">{currentUser.displayName || "My Account"}</p>
                      <p className="text-[11px] text-gray-400 truncate">{currentUser.email}</p>
                    </div>
                    {userProfile?.role === "admin" && (
                      <Link to="/admin" onClick={() => setShowUser(false)}>
                        <li className="px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors cursor-pointer flex items-center gap-2">
                          🛠️ Admin Panel
                        </li>
                      </Link>
                    )}
                    <li
                      onClick={async () => {
                        await logout();
                        setShowUser(false);
                        toast.success("Signed out successfully.");
                        navigate("/");
                      }}
                      className="px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors cursor-pointer flex items-center gap-2"
                    >
                      <FaSignOutAlt /> Sign Out
                    </li>
                  </>
                ) : (
                  <>
                    <Link to="/signin" onClick={() => setShowUser(false)}>
                      <li className="px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors cursor-pointer flex items-center gap-2">
                        <FaSignInAlt /> Sign In
                      </li>
                    </Link>
                    <Link to="/signup" onClick={() => setShowUser(false)}>
                      <li className="px-4 py-2.5 text-sm text-gray-700 hover:bg-teal-50 hover:text-teal-700 transition-colors cursor-pointer flex items-center gap-2">
                        <FaUserCircle /> Create Account
                      </li>
                    </Link>
                  </>
                )}
              </motion.ul>
            )}

            {buyingOptionsEnabled && (
              <Link to="/cart">
                <div className="relative">
                  <FaShoppingCart />
                  {products.length > 0 && (
                    <span className="absolute font-titleFont top-3 -right-2 text-xs w-4 h-4 flex items-center justify-center rounded-full bg-primeColor text-white">
                      {products.length}
                    </span>
                  )}
                </div>
              </Link>
            )}
            <BsSuitHeartFill />
          </div>
        </Flex>
      </div>
    </div>
  );
};

export default HeaderBottom;
